import { Button } from '@/shared/components/ui/Button';
import { confirmacaoBate, useConfirmStore } from '@/shared/lib/confirm';
import { useEffect, useState } from 'react';

/**
 * Renderiza o dialog de confirmação ativo (lê do store).
 * Montado uma vez no AppShell — múltiplas chamadas a `confirm({...})` reusam
 * essa mesma instância, sem cadastro de listeners ou portals manuais.
 *
 * Estilo do projeto: backdrop preto translúcido, card branco com borda sutil
 * e sombra, botões na cor brand (primary) ou danger conforme variant.
 */
export function ConfirmDialog() {
  const { open, options, resolveAndClose } = useConfirmStore();
  const [digitado, setDigitado] = useState('');
  const exigido = options?.requireText;
  const liberado = confirmacaoBate(digitado, exigido);

  // Campo limpo a cada abertura — senão o texto da confirmação anterior
  // liberaria o botão da próxima antes de a pessoa ler qualquer coisa.
  useEffect(() => {
    if (open) setDigitado('');
  }, [open]);

  // ESC = cancelar; ENTER = confirmar. Atalhos esperados em qualquer dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') resolveAndClose(false);
      // ENTER só confirma se a palavra já estiver certa — senão o atalho
      // passaria por cima justamente do atrito que o campo existe pra criar.
      if (e.key === 'Enter' && liberado) resolveAndClose(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, resolveAndClose, liberado]);

  if (!open || !options) return null;

  const variant = options.variant ?? 'primary';
  const confirmText = options.confirmText ?? 'Confirmar';
  const cancelText = options.cancelText ?? 'Cancelar';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4"
      onClick={(e) => {
        // Click fora do card cancela. Click dentro não propaga (handler do card).
        if (e.target === e.currentTarget) resolveAndClose(false);
      }}
      onKeyDown={(e) => {
        // Defensa: ESC também por aqui caso o keydown global não dispare por foco.
        if (e.key === 'Escape') resolveAndClose(false);
      }}
    >
      <div className="w-full max-w-[420px] rounded-md border border-n-200 bg-n-0 p-5 shadow-lg">
        <h2 id="confirm-title" className="text-[16px] font-semibold text-n-900">
          {options.title}
        </h2>
        {options.description && (
          <p className="mt-2 whitespace-pre-line text-[13px] leading-[1.45] text-n-600">
            {options.description}
          </p>
        )}
        {exigido && (
          <label className="mt-4 block">
            <span className="text-[12px] text-n-600">
              Para confirmar, digite <strong className="text-n-900">{exigido}</strong>
            </span>
            <input
              value={digitado}
              onChange={(e) => setDigitado(e.target.value)}
              // biome-ignore lint/a11y/noAutofocus: é o próximo passo do diálogo
              autoFocus
              placeholder={exigido}
              aria-label={`Digite ${exigido} para confirmar`}
              className="mt-1.5 w-full rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] outline-none focus:border-danger-500"
            />
          </label>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => resolveAndClose(false)} type="button">
            {cancelText}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={() => resolveAndClose(true)}
            type="button"
            disabled={!liberado}
            // Com campo de confirmação o foco vai pro campo, não pro botão.
            autoFocus={!exigido}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}
